import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../stores';
import { tryRefreshToken } from '../utils/authUtils';

/** Intervallo refresh proattivo: 45 min (token backend default 60 min) */
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

/**
 * Minimum time between visibility-triggered refreshes.
 * On iOS, visibilitychange fires on every foreground resume — without throttling
 * this would call /api/auth/refresh on every phone unlock.
 */
const VISIBILITY_REFRESH_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Hook per auto-refresh del token quando "Remember me" è attivo.
 * Esegue refresh proattivo ogni 45 min e al ritorno in foreground (throttled).
 */
export function useTokenAutoRefresh(): void {
  const { isAuthenticated, rememberMe, refreshToken } = useSelector(
    (state: RootState) => state.auth
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastVisibilityRefresh = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || !rememberMe || !refreshToken) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const doRefresh = (): void => {
      tryRefreshToken().catch(() => {
        // Ignora errori: refresh proattivo, non blocca l'app
      });
    };

    intervalRef.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityRefresh.current < VISIBILITY_REFRESH_THROTTLE_MS) return;
      lastVisibilityRefresh.current = now;
      doRefresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, rememberMe, refreshToken]);
}
